import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AlbumIcon from "@mui/icons-material/Album";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import YouTube from "react-youtube";

type SongData = {
  Id: number;
  SongName: string;
  Artist: string;
  Features: string;
  URL: string;
  VideoViews: number;
  streamCount: number;
  TrackId: number;
  isWorkingVideo: string;
};

type AlbumSongsPopupProps = {
  open: boolean;
  onClose: () => void;
  albumId: number;
  albumName: string;
  artistName: string;
  albumCover?: string;
  getCommands: { getCommon: (command: string) => Promise<any> };
};

const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  // Try to extract from a full YouTube URL first
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (match) return match[1];
  // If it's already just a video ID (11 chars, valid YouTube ID characters)
  const trimmed = url.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  return null;
};

const formatNumber = (num: number): string => {
  if (!num || num === 0) return "-";
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
};

const AlbumSongsPopup = ({
  open,
  onClose,
  albumId,
  albumName,
  artistName,
  albumCover,
  getCommands,
}: AlbumSongsPopupProps) => {
  const [songs, setSongs] = useState<SongData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [activeRowId, setActiveRowId] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !albumId) return;
    setLoading(true);
    setSongs([]);
    setActiveVideoId(null);
    setActiveRowId(null);

    getCommands
      .getCommon(`albumSongs?albumId=${albumId}`)
      .then((result) => {
        if (Array.isArray(result)) {
          setSongs(result);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch album songs:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, albumId, getCommands]);

  const handlePlayToggle = (songId: number, url: string) => {
    const videoId = extractYouTubeId(url);
    if (!videoId) return;

    if (activeRowId === songId) {
      setActiveVideoId(null);
      setActiveRowId(null);
    } else {
      setActiveVideoId(videoId);
      setActiveRowId(songId);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-soft)",
          maxHeight: "85vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2.5,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          {albumCover ? (
            <img
              src={albumCover}
              alt={albumName}
              style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                objectFit: "cover",
                flexShrink: 0,
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/Images/DefaultAlbumCover.svg";
              }}
            />
          ) : (
            <AlbumIcon sx={{ fontSize: 48, color: "var(--text-faint)" }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {albumName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--text-faint)" }}
            >
              {artistName}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3, pb: 1, pt: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : songs.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", py: 6, color: "var(--text-faint)" }}
          >
            No songs found for this album.
          </Typography>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "55vh",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "var(--hover-overlay-strong)",
                borderRadius: 3,
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      width: 40,
                      px: 1,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Title
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Features
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                      <HeadphonesIcon sx={{ fontSize: 13 }} />
                      Streams
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                      <VisibilityIcon sx={{ fontSize: 13 }} />
                      YT Views
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "var(--surface-1)",
                      color: "var(--text-primary)",
                      borderBottom: "1px solid var(--border-soft)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      width: 50,
                    }}
                  >
                    Play
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {songs.map((song, index) => {
                  const videoId = extractYouTubeId(song.URL);
                  const isActive = activeRowId === song.Id;
                  const hasVideo = videoId && song.isWorkingVideo !== "false" && song.isWorkingVideo !== "0" && song.isWorkingVideo !== "no";

                  return (
                    <>
                      <TableRow
                        key={song.Id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "var(--hover-overlay)",
                          },
                          backgroundColor: isActive
                            ? "var(--accent-soft)"
                            : "transparent",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "var(--text-primary)",
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            fontSize: "0.9rem",
                            px: 1,
                          }}
                        >
                          {song.TrackId || index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "var(--text-primary)",
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            fontWeight: 500,
                            fontSize: "0.95rem",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {song.SongName}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "var(--text-primary)",
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            fontSize: "0.9rem",
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {song.Features || "-"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: "var(--text-primary)",
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            fontSize: "0.9rem",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatNumber(song.streamCount)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: "var(--text-primary)",
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            fontSize: "0.9rem",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatNumber(song.VideoViews)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            borderBottom: isActive
                              ? "none"
                              : "1px solid var(--border-soft)",
                            px: 0,
                          }}
                        >
                          {hasVideo ? (
                            <IconButton
                              size="small"
                              onClick={() => handlePlayToggle(song.Id, song.URL)}
                              sx={{
                                color: isActive ? "var(--accent)" : "var(--text-secondary)",
                                "&:hover": {
                                  color: isActive ? "var(--accent-hover)" : "var(--text-primary)",
                                  backgroundColor: "var(--hover-overlay)",
                                },
                              }}
                            >
                              {isActive ? (
                                <StopIcon fontSize="small" />
                              ) : (
                                <PlayArrowIcon fontSize="small" />
                              )}
                            </IconButton>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: "var(--text-faint)" }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Inline YouTube Player */}
                      <TableRow key={`player-${song.Id}`}>
                        <TableCell
                          colSpan={6}
                          sx={{
                            p: 0,
                            borderBottom: isActive
                              ? "1px solid var(--border-soft)"
                              : "none",
                          }}
                        >
                          <Collapse in={isActive} timeout={250} unmountOnExit>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 1.5,
                                px: 2,
                                backgroundColor: "rgba(0,0,0,0.2)",
                              }}
                            >
                              {activeVideoId && isActive && (
                                <YouTube
                                  videoId={activeVideoId}
                                  opts={{
                                    height: "200",
                                    width: "356",
                                    playerVars: {
                                      autoplay: 1,
                                      modestbranding: 1,
                                      rel: 0,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* Footer - Powered by Music Randomizer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          borderTop: "1px solid var(--border-soft)",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "var(--text-faint)", fontSize: "0.7rem" }}
        >
          Powered by{" "}
          <Link
            href="https://musicrandomizer.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "var(--text-faint)",
              textDecoration: "none",
              "&:hover": {
                color: "var(--text-secondary)",
                textDecoration: "underline",
              },
            }}
          >
            Music Randomizer
          </Link>
        </Typography>
      </Box>
    </Dialog>
  );
};

export default AlbumSongsPopup;
